/**
 * Centralized storage service for localStorage operations
 */

class StorageService {
  constructor() {
    this.listeners = new Map();
  }

  /**
   * Get item from localStorage with error handling
   */
  getItem(key, defaultValue = null) {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      console.error(`Failed to get localStorage item "${key}":`, error);
      return defaultValue;
    }
  }

  /**
   * Set item in localStorage with error handling
   */
  setItem(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
      this.notifyListeners(key, value);
      return true;
    } catch (error) {
      console.error(`Failed to set localStorage item "${key}":`, error);
      return false;
    }
  }

  /**
   * Remove item from localStorage
   */
  removeItem(key) {
    try {
      localStorage.removeItem(key);
      this.notifyListeners(key, null);
      return true;
    } catch (error) {
      console.error(`Failed to remove localStorage item "${key}":`, error);
      return false;
    }
  }

  /**
   * Add listener for storage changes
   */
  addListener(key, callback) {
    if (!this.listeners.has(key)) {
      this.listeners.set(key, new Set());
    }
    this.listeners.get(key).add(callback);

    // Return unsubscribe function
    return () => {
      const keyListeners = this.listeners.get(key);
      if (keyListeners) {
        keyListeners.delete(callback);
        if (keyListeners.size === 0) {
          this.listeners.delete(key);
        }
      }
    };
  }

  /**
   * Notify listeners of storage changes
   */
  notifyListeners(key, value) {
    const keyListeners = this.listeners.get(key);
    if (keyListeners) {
      keyListeners.forEach((callback) => {
        try {
          callback(value, key);
        } catch (error) {
          console.error(`Error in storage listener for key "${key}":`, error);
        }
      });
    }
  }

  /**
   * Trip planner specific methods
   */
  getTripPlannerIds() {
    return this.getItem("trip_planner_ids", []);
  }

  setTripPlannerIds(ids) {
    const success = this.setItem("trip_planner_ids", ids);
    if (success) {
      // Dispatch custom event for backward compatibility
      window.dispatchEvent(
        new CustomEvent("tripPlannerUpdate", {
          detail: { ids },
        }),
      );
    }
    return success;
  }

  addToTripPlanner(id) {
    const ids = this.getTripPlannerIds();
    if (!ids.includes(id)) {
      ids.push(id);
      return this.setTripPlannerIds(ids);
    }
    return true;
  }

  removeFromTripPlanner(id) {
    const ids = this.getTripPlannerIds();
    const filteredIds = ids.filter((existingId) => existingId !== id);
    if (filteredIds.length !== ids.length) {
      return this.setTripPlannerIds(filteredIds);
    }
    return true;
  }

  toggleTripPlannerItem(id) {
    const ids = this.getTripPlannerIds();
    if (ids.includes(id)) {
      return this.removeFromTripPlanner(id);
    } else {
      return this.addToTripPlanner(id);
    }
  }

  isTripPlannerItem(id) {
    return this.getTripPlannerIds().includes(id);
  }
}

// Create and export singleton instance
export const storageService = new StorageService();

// Export class for testing or multiple instances
export { StorageService };
